import type { LucideIcon } from 'lucide-react';
import { LayoutGrid, Settings } from 'lucide-react';
import { Customer } from '@/shared/types/customerTypes';

export enum CustomerDetailsTab {
  DASHBOARD = 'Dashboard',
  SETTINGS = 'Settings',
}

export interface CustomerDetailsTabConfig {
  tab: CustomerDetailsTab;
  slug: string;
  icon: LucideIcon;
}

/** Single source of truth for customer detail tabs (slug, enum, icon). */
export const CUSTOMER_DETAILS_TAB_CONFIG: readonly CustomerDetailsTabConfig[] = [
  { tab: CustomerDetailsTab.DASHBOARD, slug: 'dashboard', icon: LayoutGrid },
  { tab: CustomerDetailsTab.SETTINGS, slug: 'settings', icon: Settings },
] as const;

const TAB_TO_SLUG = Object.fromEntries(
  CUSTOMER_DETAILS_TAB_CONFIG.map(({ tab, slug }) => [tab, slug]),
) as Record<CustomerDetailsTab, string>;

const SLUG_TO_TAB = Object.fromEntries(
  CUSTOMER_DETAILS_TAB_CONFIG.map(({ tab, slug }) => [slug, tab]),
) as Record<string, CustomerDetailsTab>;

export function getCustomerTabSlug(tab?: CustomerDetailsTab | undefined): string {
  if (!tab) return TAB_TO_SLUG[CustomerDetailsTab.DASHBOARD];
  return TAB_TO_SLUG[tab] ?? TAB_TO_SLUG[CustomerDetailsTab.DASHBOARD];
}

export function getCustomerTabFromSlug(slug?: string | undefined): CustomerDetailsTab {
  if (!slug) return CustomerDetailsTab.DASHBOARD;
  return SLUG_TO_TAB[slug] ?? CustomerDetailsTab.DASHBOARD;
}

export function isValidCustomerDetailsTabSlug(slug: string): boolean {
  return slug in SLUG_TO_TAB;
}

/** Settings is only available when the customer has self-service enabled. */
export function getVisibleCustomerDetailsTabs(customer: Customer): CustomerDetailsTabConfig[] {
  if (customer?.isSelfServiceAllowed === true) {
    return [...CUSTOMER_DETAILS_TAB_CONFIG];
  }
  return CUSTOMER_DETAILS_TAB_CONFIG.filter((tab) => tab.tab !== CustomerDetailsTab.SETTINGS);
}

export function isCustomerDetailsTabAllowed(
  tab: CustomerDetailsTab,
  customer: Customer,
): boolean {
  return getVisibleCustomerDetailsTabs(customer).some((entry) => entry.tab === tab);
}
